import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { DeviceSensorClient } from '@frontend/device/api';
import { Device, DeviceType, SensorLevel, SensorType } from '@frontend/device/types';
import { MqttBroker } from '@frontend/pub-sub';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { KeyboardProps } from './keyboard.component';

interface ViewProps {
    relevantDevices: Device[];
    selectedDevice: Device | null;
    setSelectedDevice: (device: Device | null) => void;

    input: string;
    setInput: (input: string) => void;
    sendInput: () => void;

    messages: { incoming: boolean; message: string; info: string; timestamp: string; data: any }[];
}

const useKeyboard = (props: KeyboardProps): ViewProps => {
    const intl = useIntl();
    const subscriberId = `keyboard-emulator-${props.iot.id}`;
    const relevantDevices = props.devices.filter((device) => device.type === DeviceType.KEYBOARD);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(relevantDevices.length > 0 ? relevantDevices[0] : null);
    const [input, setInput] = useState<string>('');
    const [messages, setMessages] = useState<{ incoming: boolean; message: string; info: string; timestamp: string; data: any }[]>([]);
    const addMessage = (incoming: boolean, message: string, info: string, data: any) => {
        const time = formatDateTimeWithLocale(new Date(), intl.locale, DateFormat.HH_MM_SS_MS);
        setMessages((prev) => [{ incoming, message, info, timestamp: time, data }, ...prev]);
    };

    const onKeyboardInput = (message: string, data: any) => {
        addMessage(true, message, 'Keyboard input: ' + data.data.input, data);
    };

    const callback = (message: string, data: any) => {
        if (message === 'device_sensor_received' && data?.type === SensorType.TEXT) onKeyboardInput(message, data);
    };

    const sendInput = async () => {
        if (!selectedDevice) return;
        const body = {
            level: SensorLevel.INFO,
            type: SensorType.TEXT,
            data: {
                input
            }
        };
        addMessage(false, 'Sent keyboard input', 'Input: ' + input, body);
        return DeviceSensorClient.postDeviceSensor(props.iot.account_id, props.iot.id, selectedDevice.id, body, true, props.token).catch((error) => {
            console.error('Failed to post keyboard input sensor data:', error);
        });
    };

    useEffect(() => {
        MqttBroker.getInstance().subscribe(subscriberId, `iots/${props.iot.id}/sensor`, callback);
        return () => {
            MqttBroker.getInstance().unSubscribe(subscriberId, `iots/${props.iot.id}/sensor`);
        };
    }, []);

    return {
        relevantDevices,
        selectedDevice,
        setSelectedDevice,
        input,
        setInput,
        sendInput,
        messages
    };
};

export default useKeyboard;
